import type { ActivityDTO } from 'src/types/dto';

import type { ActivityDependencyFormValue } from 'src/sections/visit/components/activity-dependencies-field';

type Edge = { activityId: number; dependsOnActivityId: number };

function buildAdjacency(edges: Edge[]): Map<number, number[]> {
  const adjacency = new Map<number, number[]>();
  for (const { activityId, dependsOnActivityId } of edges) {
    const list = adjacency.get(activityId) ?? [];
    if (!list.includes(dependsOnActivityId)) {
      list.push(dependsOnActivityId);
    }
    adjacency.set(activityId, list);
  }
  return adjacency;
}

function prerequisitePathReaches(
  fromId: number,
  targetId: number,
  adjacency: Map<number, number[]>
): boolean {
  const visited = new Set<number>();
  const stack = [fromId];
  while (stack.length > 0) {
    const current = stack.pop()!;
    if (current === targetId) return true;
    if (visited.has(current)) continue;
    visited.add(current);
    stack.push(...(adjacency.get(current) ?? []));
  }
  return false;
}

/** Project dependency edges, using form state for the activity being edited. */
export function buildProjectDependencyEdges(
  projectActivities: ActivityDTO[],
  currentActivityId: number,
  pendingDependencies: ActivityDependencyFormValue[]
): Edge[] {
  const edges: Edge[] = [];
  for (const activity of projectActivities) {
    if (activity.id === currentActivityId) {
      for (const dep of pendingDependencies) {
        edges.push({
          activityId: currentActivityId,
          dependsOnActivityId: dep.dependsOnActivityId,
        });
      }
    } else {
      for (const dep of activity.dependsOnActivities ?? []) {
        edges.push({
          activityId: activity.id,
          dependsOnActivityId: dep.id,
        });
      }
    }
  }
  return edges;
}

/**
 * True if adding `candidatePrerequisiteId` would create a circular dependency.
 */
export function addingPrerequisiteCreatesCycle(
  activityId: number,
  candidatePrerequisiteId: number,
  pendingDependencies: ActivityDependencyFormValue[],
  projectActivities: ActivityDTO[]
): boolean {
  if (candidatePrerequisiteId === activityId) {
    return true;
  }

  const edgesExcludingCurrentOutgoing = buildProjectDependencyEdges(
    projectActivities,
    activityId,
    pendingDependencies
  ).filter((e) => e.activityId !== activityId);

  const adjacency = buildAdjacency(edgesExcludingCurrentOutgoing);
  return prerequisitePathReaches(candidatePrerequisiteId, activityId, adjacency);
}
